import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Calendar, Edit2, Save, Plus, Trash2, Map as MapIcon, Image } from 'lucide-react';
import TripMap from './TripMap';
import TripTimeline from './TripTimeline';
import tripService from '../services/tripService';
import './TripScrapbook.css';

const TripScrapbook = () => {
    const location = useLocation();
    // Initialize trips from service
    const [allTrips, setAllTrips] = useState(tripService.getTrips());
    const [selectedTrip, setSelectedTrip] = useState(allTrips[0]);
    const [activePhotoId, setActivePhotoId] = useState(null);
    const [viewMode, setViewMode] = useState('overview'); // 'overview' | 'gallery'
    const [isEditing, setIsEditing] = useState(false);
    const mapRef = useRef(null);

    // Handle trip selection from navigation state (if passed from App.js)
    useEffect(() => {
        if (location.state?.tripId) {
            const trip = allTrips.find(t => t.id === location.state.tripId);
            if (trip) {
                // Merge state data (description, highlights) with trip data
                // Note: In a real app, we might want to prioritize the saved data over the nav state,
                // or handle conflicts. For now, we'll assume the service has the latest truth,
                // but if nav state passes specific overrides (like from a "Create Trip" flow), we use them.
                // However, since we are persisting, let's trust the service data primarily.
                setSelectedTrip(trip);
                setViewMode('overview');
            }
        }
    }, [location.state, allTrips]);

    // ... (reset useEffect) ...

    // ... (zoom useEffect) ...

    const handleTripChange = (e) => {
        const tripId = e.target.value;
        const trip = allTrips.find(t => t.id === tripId);
        setSelectedTrip(trip);
    };

    const handleNext = () => {
        if (!selectedTrip) return;

        if (viewMode === 'overview') {
            setViewMode('gallery');
            if (selectedTrip.photos.length > 0) {
                setActivePhotoId(selectedTrip.photos[0].id);
            }
            return;
        }

        if (!activePhotoId) return;
        const currentIndex = selectedTrip.photos.findIndex(p => p.id === activePhotoId);
        if (currentIndex < selectedTrip.photos.length - 1) {
            setActivePhotoId(selectedTrip.photos[currentIndex + 1].id);
            scrollToPhoto(selectedTrip.photos[currentIndex + 1].id);
        }
    };

    const handlePrev = () => {
        if (!selectedTrip) return;

        if (viewMode === 'gallery') {
            const currentIndex = selectedTrip.photos.findIndex(p => p.id === activePhotoId);
            if (currentIndex === 0 || currentIndex === -1) {
                setViewMode('overview');
                setActivePhotoId(null);
            } else {
                setActivePhotoId(selectedTrip.photos[currentIndex - 1].id);
                scrollToPhoto(selectedTrip.photos[currentIndex - 1].id);
            }
        }
    };

    const scrollToPhoto = (id) => {
        const element = document.getElementById(`photo-${id}`);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const handleUpdateTrip = (updates) => {
        const updatedTrip = { ...selectedTrip, ...updates };
        setSelectedTrip(updatedTrip);

        // Save to persistence
        tripService.saveTrip(updatedTrip);

        // Update local list state so dropdowns etc stay in sync
        setAllTrips(prev => prev.map(t => t.id === updatedTrip.id ? updatedTrip : t));
    };

    const handleAddHighlight = () => {
        const newHighlights = [...(selectedTrip.highlights || []), "New Highlight"];
        handleUpdateTrip({ highlights: newHighlights });
    };

    const handleRemoveHighlight = (index) => {
        const newHighlights = selectedTrip.highlights.filter((_, i) => i !== index);
        handleUpdateTrip({ highlights: newHighlights });
    };

    const handleHighlightChange = (index, value) => {
        const newHighlights = [...(selectedTrip.highlights || [])];
        newHighlights[index] = value;
        handleUpdateTrip({ highlights: newHighlights });
    };

    const handlePhotoUpdate = (photoId, updates) => {
        const newPhotos = selectedTrip.photos.map(p =>
            p.id === photoId ? { ...p, ...updates } : p
        );
        handleUpdateTrip({ photos: newPhotos });
    };

    const [mobileTab, setMobileTab] = useState('photos'); // 'photos' | 'map'

    // ... existing useEffects ...

    return (
        <div className={`trip-scrapbook mobile-tab-${mobileTab}`}>
            <header className="scrapbook-header">
                <div className="header-left">
                    <Link to="/" className="back-btn">
                        <ArrowLeft size={20} />
                        Back to Map
                    </Link>
                    <div className="header-title">
                        <h1>Trip Scrapbook</h1>
                    </div>
                </div>

                <div className="header-controls">
                    {/* Mobile View Toggle */}
                    <div className="mobile-view-toggle">
                        <button
                            className={`toggle-btn ${mobileTab === 'photos' ? 'active' : ''}`}
                            onClick={() => setMobileTab('photos')}
                            title="Photos View"
                        >
                            <Image size={18} />
                        </button>
                        <button
                            className={`toggle-btn ${mobileTab === 'map' ? 'active' : ''}`}
                            onClick={() => setMobileTab('map')}
                            title="Map View"
                        >
                            <MapIcon size={18} />
                        </button>
                    </div>

                    <div className="nav-controls">
                        <button onClick={handlePrev} disabled={!selectedTrip || (viewMode === 'overview')} title="Previous">
                            <ChevronLeft size={20} />
                        </button>
                        <span className="photo-counter">
                            {viewMode === 'overview' ? 'Overview' :
                                (selectedTrip && activePhotoId ? `${selectedTrip.photos.findIndex(p => p.id === activePhotoId) + 1} / ${selectedTrip.photos.length}` : '')}
                        </span>
                        <button onClick={handleNext} disabled={!selectedTrip || (viewMode === 'gallery' && selectedTrip.photos.findIndex(p => p.id === activePhotoId) === selectedTrip.photos.length - 1)} title="Next">
                            <ChevronRight size={20} />
                        </button>
                    </div>

                    <div className="trip-selector-wrapper" title="Switch Trip">
                        <Calendar className="trip-selector-icon" size={20} />
                        <select value={selectedTrip?.id} onChange={handleTripChange} className="trip-selector">
                            {allTrips.map(trip => (
                                <option key={trip.id} value={trip.id}>
                                    {trip.title} ({trip.startDate})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </header>

            <div className="scrapbook-content">
                <div className={`timeline-container ${mobileTab === 'map' ? 'mobile-hidden' : ''}`}>
                    {viewMode === 'overview' ? (
                        <div className="trip-overview">
                            <div className="overview-header-actions">
                                {isEditing ? (
                                    <button className="icon-btn save-btn" onClick={() => setIsEditing(false)} title="Save Changes">
                                        <Save size={20} />
                                    </button>
                                ) : (
                                    <button className="icon-btn edit-btn" onClick={() => setIsEditing(true)} title="Edit Trip">
                                        <Edit2 size={20} />
                                    </button>
                                )}
                            </div>

                            {isEditing ? (
                                <div className="edit-mode-container">
                                    <input
                                        className="edit-title-input"
                                        value={selectedTrip?.title}
                                        onChange={(e) => handleUpdateTrip({ title: e.target.value })}
                                    />
                                    <div className="trip-dates">
                                        <Calendar size={16} />
                                        {selectedTrip?.startDate} - {selectedTrip?.endDate}
                                    </div>
                                    <textarea
                                        className="edit-description-input"
                                        value={selectedTrip?.description}
                                        onChange={(e) => handleUpdateTrip({ description: e.target.value })}
                                        rows={4}
                                    />

                                    <div className="edit-highlights-section">
                                        <h3>Highlights</h3>
                                        {selectedTrip?.highlights?.map((highlight, index) => (
                                            <div key={index} className="edit-highlight-item">
                                                <input
                                                    value={highlight}
                                                    onChange={(e) => handleHighlightChange(index, e.target.value)}
                                                />
                                                <button onClick={() => handleRemoveHighlight(index)} className="remove-btn">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                        <button onClick={handleAddHighlight} className="add-highlight-btn">
                                            <Plus size={16} /> Add Highlight
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <h2>{selectedTrip?.title}</h2>
                                    <div className="trip-dates">
                                        <Calendar size={16} />
                                        {selectedTrip?.startDate} - {selectedTrip?.endDate}
                                    </div>
                                    <p className="trip-description">
                                        {selectedTrip?.description || "A wonderful journey through " + selectedTrip?.title + ". Explore the memories and locations captured during this trip."}
                                    </p>

                                    {selectedTrip?.highlights && selectedTrip.highlights.length > 0 && (
                                        <div className="trip-highlights-display">
                                            <h3>Highlights</h3>
                                            <ul>
                                                {selectedTrip.highlights.map((h, i) => (
                                                    <li key={i}>{h}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    <div className="trip-stats">
                                        <div className="stat-item">
                                            <span className="stat-value">{selectedTrip?.photos.length}</span>
                                            <span className="stat-label">Photos</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-value">{new Set(selectedTrip?.photos.filter(p => p.lat).map(p => `${p.lat},${p.lng}`)).size}</span>
                                            <span className="stat-label">Locations</span>
                                        </div>
                                    </div>
                                    <button className="start-gallery-btn" onClick={handleNext}>
                                        Start Gallery <ChevronRight size={16} />
                                    </button>
                                </>
                            )}
                        </div>
                    ) : (
                        <TripTimeline
                            trip={selectedTrip}
                            activePhotoId={activePhotoId}
                            onPhotoInView={setActivePhotoId}
                            onPhotoUpdate={handlePhotoUpdate}
                        />
                    )}
                </div>
                <div className={`scrapbook-map-container ${mobileTab === 'photos' ? 'mobile-hidden' : ''}`}>
                    <TripMap
                        trip={selectedTrip}
                        activePhotoId={activePhotoId}
                        onMarkerClick={(id) => {
                            setViewMode('gallery');
                            setActivePhotoId(id);
                            // On mobile, switch to photos tab when marker is clicked to see the photo
                            if (window.innerWidth <= 768) {
                                setMobileTab('photos');
                            }
                        }}
                        ref={mapRef}
                    />
                </div>
            </div>
        </div>
    );
};

export default TripScrapbook;
